import { Directive, ElementRef, EventEmitter, inject, OnDestroy, computed, signal } from '@angular/core';
import { DetailElement, DetailsElementData, LijstElement, LijstElementData } from '../Details';
import { CompoundFilterExpression, DataService, FilterExpression, ListOptions, TableResponse } from '../../services/data.service';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { formatDate } from '@angular/common';
import { filter, map, scan, startWith, switchMap, take, tap } from 'rxjs/operators';
import { isNil, isUndefined } from 'lodash-es';
import { DetailsDashboard } from './details.dashboard';
import { FilterService } from '../../services/filter.service';
import { ExportService } from '../../services/export.service';
import { LoadingService, LoadingType } from '../../services/loading.service';
import { QueryParamStateService } from '../../services/query-param-state.service';
import { PageStateService, PsName } from '../../services/page-state.service';
import { LeerlingSelectieService } from '../../services/leerling-selectie.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { isDefined } from '@cumlaude/shared-utils';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { NavigatieVoorkeurService } from '../../services/navigatie-voorkeur.service';

export function convertDate(content: string): string {
	if (isNil(content)) return '-';
	else return formatDate(content, 'dd-MM-yyyy', 'nl-NL');
}

export function convert(content: string): string {
	if (isNil(content)) return '-';
	else return content;
}

const defaultLimit = 100;

@Directive()
export abstract class BaseDetailsPanelComponent<L extends LijstElement, D extends DetailElement> implements OnDestroy {
	readonly dataService = inject(DataService);
	readonly route = inject(ActivatedRoute);
	readonly filterService = inject(FilterService);
	readonly exportService = inject(ExportService);
	readonly loadingService = inject(LoadingService);
	readonly qp = inject(QueryParamStateService);
	readonly leerlingSelectieService = inject(LeerlingSelectieService);
	protected readonly pageStateService = inject(PageStateService);
	protected readonly toastr = inject(ToastrService);
	protected readonly navigatieVoorkeurService = inject(NavigatieVoorkeurService);

	selectie = this.qp.signal('selectie');

	schooljaar = this.qp.signal('schooljaar');

	readonly elementen = signal<L[]>([]);

	readonly selectedLijstElement = signal<L | undefined>(undefined);

	readonly total = signal<number | undefined>(undefined);

	readonly selectedDetailElement = signal<D | undefined>(undefined);

	readonly detailsData = computed(() => {
		const detail = this.selectedDetailElement();
		if (!detail) return undefined;
		return this.getDetailsElementData(detail);
	});

	readonly currentIndex = computed(() => {
		const selectie = this.selectie();
		if (selectie === undefined) return -1;
		return this.elementen().findIndex((element) => this.getElementKey(element) === selectie);
	});

	readonly showNext = computed(() => {
		return this.elementen().length > 1 && this.currentIndex() > 0;
	});

	readonly showPrevious = computed(() => {
		const total = this.total();
		if (!total) return false;

		return this.elementen().length > 1 && this.currentIndex() < total - 1;
	});

	readonly title = computed(() => {
		const title = this.getTitle();
		const total = this.total();
		if (total !== undefined) return `${title} (${total})`;
		return title;
	});

	fetch$ = new EventEmitter<number>();

	from: string | undefined = undefined;

	nextFetchReceived$ = new EventEmitter();

	changeElement$ = new EventEmitter<boolean>();

	searchInput$ = new BehaviorSubject<string>('');

	showSpinner$: Observable<boolean>;

	limit: { lim?: number } | undefined;

	protected childDashboard?: DetailsDashboard<D>;

	protected subscriptions: Subscription[] = [];

	constructor() {
		this.showSpinner$ = this.loadingService.shouldShowLoadingIndicator(LoadingType.TABLE);

		const { from } = this.route.snapshot.queryParams;
		this.from = from;

		const selectieExpressions = this.getSelectieExpressions();

		this.subscriptions.push(
			combineLatest([this.searchInput$, selectieExpressions])
				.pipe(
					tap(() => {
						this.selectedLijstElement.set(undefined);
						this.total.set(undefined);

						// als er bij het laden van het dashboard al een selectie is: de hele lijst ophalen ipv de eerste 100
						if (isUndefined(this.limit) && isDefined(this.selectie())) {
							this.limit = {};
						} else {
							this.limit = { lim: defaultLimit };
						}
					}),
					switchMap(([searchInput, selectieExpressions]) =>
						this.fetch$.pipe(
							startWith(0),
							filter((off) => off !== this.total()),
							switchMap((off) => {
								const zoekFilter = this.getFilters(searchInput);
								const f = new CompoundFilterExpression([
									...(zoekFilter ? [zoekFilter] : []),
									...(selectieExpressions?.length > 0 ? [new CompoundFilterExpression(selectieExpressions, 'or')] : []),
								]);
								return this.getLijstData({ ...this.limit!, off, f });
							}),
							tap((tableResponse) => {
								if (tableResponse.total !== undefined) this.total.set(tableResponse.total);
							}),
							map((tableResponse) => tableResponse.rows),
							scan((acc, cur) => [...acc, ...cur])
						)
					)
				)
				.subscribe((elementen) => {
					this.elementen.set(elementen);
					if (elementen.length > 0) {
						const selectie = this.selectie();
						if (selectie === undefined || !this.elementen().some((element) => this.getElementKey(element) === selectie))
							this.selectElement(elementen[0]);
						else if (this.selectedLijstElement() === undefined) this.setSelectedLijstElement(selectie);
					}
					this.nextFetchReceived$.emit();
				}),
			toObservable(this.selectie)
				.pipe(
					filter(isDefined),
					switchMap((key) => {
						this.setSelectedLijstElement(key);
						return this.getDetailsData(key);
					})
				)
				.subscribe((selectedDetail) => {
					const detail = selectedDetail.rows[0];
					if (!detail) {
						this.toastr.warning(`De gevraagde ${this.getType()} kon niet (meer) gevonden worden.`);
						this.selectedDetailElement.set(undefined);
						return;
					}
					this.selectedDetailElement.set(detail);
					this.childDashboard!.setSelected(detail, this.schooljaar());
				}),
			this.changeElement$.subscribe((next) => this.gotoNextOrPrevious(next))
		);
	}

	setSelectedLijstElement<B>(key: B) {
		const element = this.elementen().find((element) => this.getElementKey(element) === key);
		this.selectedLijstElement.set(element);

		const data = element ? this.getLijstElementData(element) : null;
		const subject = data ? `${data.title} (${data.subTitle})` : 'Onbekend';
		this.pageStateService.dispatch(PsName.subject, subject);
	}

	abstract getUrlPart(): string;

	abstract getElementKey(element: L): number;

	selectElement(element: L) {
		this.qp.dispatch('selectie', this.getElementKey(element));
	}

	ngOnDestroy() {
		for (const sub of this.subscriptions) sub.unsubscribe();
	}

	onActivate(componentReference: DetailsDashboard<D>) {
		this.childDashboard = componentReference;
		const detail = this.selectedDetailElement();
		if (detail) this.childDashboard.setSelected(detail, this.schooljaar());
	}

	startExport(triggerElement?: ElementRef) {
		this.exportService
			.startExport(
				triggerElement,
				this.childDashboard!.factTable,
				this.childDashboard!.attOverrides,
				this.childDashboard!.exportTypes,
				this.getLegendaOptions(),
				this.childDashboard!.customExportItems
			)
			.subscribe((exportOptions) => {
				this.childDashboard!.doDetailsExport(exportOptions);
			});
	}

	private getLegendaOptions() {
		return this.childDashboard?.legendaComponent?.options();
	}

	abstract getTitle(): string;

	abstract getType(): string;

	abstract getLijstElementData(input: L): LijstElementData;

	abstract getDetailsElementData(input: D): DetailsElementData;

	abstract getLijstData(options: Partial<ListOptions>): Observable<TableResponse<L>>;

	abstract getDetailsData(key: number): Observable<TableResponse<D>>;

	abstract getFilters(searchInput: string): FilterExpression | undefined;

	abstract isSelectionVisible(): boolean;

	/**
	 * De selecties die op de lijst van dit paneel worden toegepast: standaard de leerlingselectie ('details-list' en 'adhoc').
	 */
	protected getSelectieExpressions(): Observable<FilterExpression[]> {
		return this.leerlingSelectieService
			.observeDetailsList()
			.pipe(switchMap((selectieIds) => this.leerlingSelectieService.getExpressions(selectieIds)));
	}

	private gotoNextOrPrevious(next: boolean) {
		const currentIndex = this.currentIndex();

		let index;
		if (next) index = currentIndex + 1;
		else index = currentIndex - 1;

		const elementen = this.elementen();
		if (index >= 0 && index < elementen.length) this.selectElement(elementen[index]);
		else if (index >= 0 && index < this.total()!) {
			const nextIndex = index;
			this.nextFetchReceived$.pipe(take(1)).subscribe(() => this.selectElement(this.elementen()[nextIndex]));
			this.fetch$.emit(elementen.length);
		}
	}
}
